import { createSelector } from '@reduxjs/toolkit';
import { measurementsSlice } from './measurements.slice';
import { readerTaskSlice } from '../readerTask/readerTask.slice';
import { clinicalTrialInventorySlice } from '../clinicalTrialInventory/clinicalTrialInventory.slice';
import {
  AnnotationInstanceObjectType,
  MeasurementInstanceObjectType,
} from '@dynamika-shared/types/measurements';
import { RootState } from '../store';
import { readerSubTaskSlice } from '../readerSubTask/readerSubTask.slice';
import { getAnnotations } from '@dynamika-shared/utils/measurements/normalizers';

const { selectReaderTask, selectCurrentVisit } = readerTaskSlice.selectors;
const {
  selectCurrentReaderSubTask,
  selectCurrentVisit: selectCurrentSubTaskVisit,
} = readerSubTaskSlice.selectors;
const { selectClinicalTrialInventory } = clinicalTrialInventorySlice.selectors;

const {
  getMeasurements,
  getActiveMeasurement,
  getScreenshot,
} = measurementsSlice.selectors;

/**
 * Find the one stored measurement that a form is pointing at, given its
 * `code` (e.g. "TARGET_ENHANCING_LESION") and its `index` (the Nth lesion of
 * that kind).
 *
 * Why it is not a one-liner — it does TWO lookups, each with a fallback:
 *
 *   STEP 1 — which inventory row?
 *     The clinical-trial inventory can list the SAME code more than once (a
 *     grouped patient → follow-up task has one row per scoring form). We pick
 *     the row that belongs to the form the reader is currently looking at
 *     (active scoring code → its scoringFormId). If we can't resolve the active
 *     form, we fall back to the first row with that code.
 *
 *   STEP 2 — which stored measurement?
 *     Match the measurement whose `measurementId` equals the chosen row's `id`
 *     AND whose position matches `index`. Position can come from two places:
 *       • the viewer's `instrumentStateJson.measurementState.toolIndex`, or
 *       • the row's top-level `index` (used by previous-visit copies that have
 *         no viewer state yet).
 *     We try toolIndex first, fall back to the top-level index.
 *
 * Getting either fallback wrong produces an empty `measurementId` in the lock
 * URL — hence the care. Returns the measurement, or `undefined` if none matches.
 */
export const selectMeasurementByCode = createSelector(
  [
    getMeasurements,
    selectClinicalTrialInventory,
    readerSubTaskSlice.selectors.selectCurrentVisit,
    readerTaskSlice.selectors.selectCurrentVisit,
    (state, code: string, index: number) => ({
      code,
      index,
    }),
  ],
  (measurements, inventory, subTaskVisit, visit, measurementParams) => {
    // Grouped tasks (e.g. RANO patient → followup) have multiple inventory
    // rows with the same `code` but distinct `scoringFormId`s. Picking the
    // first match returns the baseline-scope row whose `id` does not
    // appear in `measurements[].measurementId` for the follow-up task, so
    // the second `find` returns undefined and the lock URL ends up with an
    // empty `measurementId` segment. Scope the inventory lookup to the
    // active scoring form when we can resolve it.
    const activeScoringCode = subTaskVisit?.scoringCode ?? visit?.scoringCode;
    const activeScoringFormId = inventory.scoringForms?.find(
      f => f.code === activeScoringCode
    )?.id;
    const candidateConfigs = inventory.measurements.filter(
      m => m.code === measurementParams.code
    );
    const config = activeScoringFormId
      ? (candidateConfigs.find(m => m.scoringFormId === activeScoringFormId) ??
        candidateConfigs[0])
      : candidateConfigs[0];
    // For previous-visit copies the BE row arrives with
    // `instrumentStateJson === null` (no viewer state yet) but a real
    // top-level `index`. The original lookup used
    // `instrumentStateJson.measurementState.toolIndex` exclusively, so
    // those rows were invisible and the lock URL got an empty
    // `measurementId` segment. Match on top-level `index` first, fall back
    // to the viewer toolIndex.
    return measurements.find(m => {
      if (m.measurementId !== config?.id) return false;
      const toolIndex = m.instrumentStateJson?.measurementState?.toolIndex;
      if (toolIndex !== undefined) {
        return toolIndex === measurementParams.index;
      }
      return m.index === measurementParams.index;
    });
  }
);

export const selectMeasurementsByIds = createSelector(
  [
    getMeasurements,
    selectClinicalTrialInventory,
    (state, measurementIds: string[]) => ({
      measurementIds,
    }),
  ],
  (measurements, inventory, { measurementIds }) => {
    return measurements.filter(m => measurementIds.includes(m.id));
  }
);

export const selectStoreDataRelatedToMeasurements = createSelector(
  [
    getActiveMeasurement,
    getMeasurements,
    selectClinicalTrialInventory,
    selectReaderTask,
    selectCurrentVisit,
    selectCurrentSubTaskVisit,
    selectCurrentReaderSubTask,
  ],
  (
    activeMeasurement,
    measurements,
    inventory,
    task,
    visit,
    subTaskVisit,
    subTask
  ) => ({
    activeMeasurement,
    measurements,
    inventory,
    task,
    visit,
    subTask,
    subTaskVisit,
  })
);

export const selectDataRelatedToMeasurements = createSelector(
  [
    selectStoreDataRelatedToMeasurements,
    (
      state: RootState,
      annotation: Partial<AnnotationInstanceObjectType> | null | undefined,
      measurement: Partial<MeasurementInstanceObjectType> | undefined
    ) => ({ measurement, annotation }),
  ],
  (
    {
      activeMeasurement,
      measurements,
      inventory,
      task,
      visit,
      subTask,
      subTaskVisit,
    },
    { annotation, measurement }
  ) => {
    // Look up the parent measurement by any cornerstone UID it owns — for
    // multi-annotation records the incoming annotation might match an entry
    // in `annotations[]`, not the singular field.
    //
    // Guard both identity matches against undefined. On a viewer delete the
    // `annotation` arg is undefined, so an unguarded `uid === undefined` would
    // match the FIRST measurement that lacks a uid — e.g. an orphaned / legacy
    // Circle ROI record (D7-7080) — and the delete would target that wrong
    // (often locked) record instead of the clicked circle. Only match on real ids.
    const currentMeasurement = measurements.find(m => {
      if (
        annotation?.annotationUID &&
        getAnnotations(m).some(
          a => a.annotationUID === annotation.annotationUID
        )
      ) {
        return true;
      }
      return (
        !!measurement?.uid &&
        m.instrumentStateJson?.measurementState?.uid === measurement?.uid
      );
    });

    let measurementConfig = inventory?.measurements?.find(
      m => m.id === currentMeasurement?.measurementId
    );
    let instrumentConfig = inventory?.instruments?.find(
      i => i.id === currentMeasurement?.instrumentId
    );

    const isNew = !currentMeasurement && !!activeMeasurement;

    if (isNew) {
      // Resolving the config for a brand-new lesion has to survive two
      // overlapping ambiguities, so the lookup is layered most-specific first:
      //
      // 1. `measurementConfigId`/`instrumentConfigId` — the id the form already
      //    resolved from its own scoped inventory list (D7-7189). This is exact
      //    and disambiguates versioned forms that share a segment `code`
      //    (LIVER_PDFF vs LIVER_PDFF_V2), where matching by `code` alone can
      //    pick the wrong version's row.
      // 2. `code` + active scoring form — for grouped tasks (RANO patient →
      //    followup) the same `code` exists across scoring forms with distinct
      //    `scoringFormId`s. When the form did not supply an id, scope by the
      //    active visit's scoring form so we don't grab the baseline-scoped row.
      // 3. plain `code` — last-resort fallback.
      //
      // Either layer landing on the wrong row makes BE reject the lock with
      // "Wrong measurement label identifier".
      const activeScoringCode = subTaskVisit?.scoringCode ?? visit?.scoringCode;
      const activeScoringFormId = inventory?.scoringForms?.find(
        f => f.code === activeScoringCode
      )?.id;
      const matchesActiveForm = (m: { scoringFormId: string | null }) =>
        activeScoringFormId ? m.scoringFormId === activeScoringFormId : true;

      measurementConfig =
        // Layer 1 must still belong to the active scoring form. A grouped
        // patient -> followup task can hand us a `measurementConfigId` that the
        // OTHER form resolved (e.g. the patient form's TARGET_LESION id while the
        // reader is drawing on the followup visit), which BE then rejects as a
        // "Wrong measurement label identifier". Scope the exact-id match too so a
        // cross-form id falls through to the code + active-form lookup below.
        (activeMeasurement?.measurementConfigId &&
          inventory?.measurements?.find(
            m => m.id === activeMeasurement.measurementConfigId && matchesActiveForm(m)
          )) ||
        inventory?.measurements?.find(
          m => m.code === activeMeasurement?.measurementCode && matchesActiveForm(m)
        ) ||
        inventory?.measurements?.find(
          m => m.code === activeMeasurement?.measurementCode
        );

      instrumentConfig =
        (activeMeasurement?.instrumentConfigId &&
          inventory?.instruments?.find(
            i => i.id === activeMeasurement.instrumentConfigId
          )) ||
        inventory?.instruments?.find(
          i => i.code === activeMeasurement?.instrumentCode && matchesActiveForm(i)
        ) ||
        inventory?.instruments?.find(
          i => i.code === activeMeasurement?.instrumentCode
        );
    }

    return {
      measurements,
      isNew,
      activeMeasurement,
      currentMeasurement,
      measurementConfig,
      instrumentConfig,
      task,
      visit,
      subTask,
      subTaskVisit,
    };
  }
);

export const selectMeasurementScreenshot = createSelector(
  getScreenshot,
  (...rest) => rest
);
